import { createSelector } from '@ngrx/store';
import {
  BookSelectModalVM,
  BookTocVM,
  FinalExamVM,
  PaginationVM,
  ParagraphVM,
  PractiseHomeVM,
  PractiseOverviewVM,
} from '@lyceo/lyceo-pupil-app/practise/util-models';
import {
  selectBookSelectCurrentBook,
  selectBookSelectMethods,
  selectBookSelectModalBooks,
} from '../method/method.selectors';
import { selectBookSelectModalScope } from '../scope/scope.selectors';
import { selectParamBook } from '../book/book.selectors';
import {
  selectBookTocError,
  selectBookTocLoading,
  selectExercisesWithResults,
  selectFinalExamResults,
  selectPractiseHomeCard,
  selectPractiseOverviewCard,
  selectPractiseOverviewError,
  selectPractiseOverviewLoading,
  selectSegmentOptions,
  selectSelectedPractiseType,
} from './practise.selectors';
import { selectAlfredLoading } from '../exercise/exercise.selectors';
import {
  selectLastParagraph,
  selectNextParagraph,
  selectParagraphError,
  selectParamParagraph,
  selectPreviousParagraph,
} from '../paragraph/paragraph.selectors';
import {
  selectChaptersWithProgress,
  selectCombinedChaptersParagraphs,
  selectParagraphParamChapter,
} from '../chapter/chapter.selectors';
import { selectParamParagraphProgress } from '../paragraph-progress/paragraph-progress.selectors';
import { selectParamParagraphVideosWithWatched } from '../video/video.selectors';
import { selectParamBookProgress } from '../book-progress/book-progress.selectors';
import {
  selectLatestError,
  selectLatestLoading,
} from '../person-uses-book/person-uses-book.selectors';

export const selectBookSelectModalVM = createSelector(
  selectBookSelectModalScope,
  selectBookSelectMethods,
  selectBookSelectModalBooks,
  selectBookSelectCurrentBook,
  (scope, methods, books, currentBook): BookSelectModalVM => {
    return {
      scope,
      methods,
      books,
      currentBook,
    };
  },
);

export const selectFinalExamVM = createSelector(
  selectParamBook,
  selectFinalExamResults,
  selectAlfredLoading,
  (book, results, alfredLoading): FinalExamVM => {
    return {
      book,
      results,
      alfredLoading,
    };
  },
);

export const selectPaginationVM = createSelector(
  selectParamParagraph,
  selectPreviousParagraph,
  selectNextParagraph,
  selectParagraphParamChapter,
  selectParamBook,
  selectLastParagraph,
  (
    paragraph,
    previousParagraph,
    nextParagraph,
    chapter,
    book,
    lastParagraph,
  ): PaginationVM => {
    return {
      paragraph,
      previousParagraph,
      nextParagraph,
      chapter,
      book,
      lastParagraph,
    };
  },
);

export const selectParagraphVM = createSelector(
  selectParamParagraph,
  selectParamParagraphProgress,
  selectExercisesWithResults,
  selectParagraphError,
  selectAlfredLoading,
  selectParamParagraphVideosWithWatched,
  (
    paragraph,
    paragraphProgress,
    exercisesWithResults,
    paragraphError,
    alfredLoading,
    videos,
  ): ParagraphVM => {
    return {
      paragraph,
      paragraphProgress,
      exercises: exercisesWithResults,
      paragraphError: !!paragraphError,
      alfredLoading,
      videos,
    };
  },
);

export const selectBookTocVM = createSelector(
  selectBookTocLoading,
  selectBookTocError,
  selectParamBook,
  selectParamBookProgress,
  selectChaptersWithProgress,
  selectParamParagraph,
  selectCombinedChaptersParagraphs,
  (
    loading,
    error,
    book,
    bookProgress,
    chapters,
    paragraph,
    combined,
  ): BookTocVM => {
    return {
      loading,
      error,
      book,
      bookProgress,
      chapters,
      currentParagraphId: paragraph?.id,
      currentChapterId: paragraph?.chapterId,
      combinedChaptersParagraphs: combined,
    };
  },
);

export const selectPractiseHomeVM = createSelector(
  selectLatestLoading,
  selectLatestError,
  selectPractiseHomeCard,
  (loading, error, cards): PractiseHomeVM => {
    return {
      loading,
      error: !!error,
      cards,
    };
  },
);

export const selectPractiseOverviewVM = createSelector(
  selectPractiseOverviewLoading,
  selectPractiseOverviewError,
  selectSegmentOptions,
  selectSelectedPractiseType,
  selectPractiseOverviewCard,
  (
    loading,
    error,
    segmentOptions,
    selectedPractiseType,
    cards,
  ): PractiseOverviewVM => {
    return {
      loading,
      error,
      segmentOptions,
      selectedPractiseType,
      cards,
    };
  },
);
